import { watch } from '@vue/composition-api';
import Vue from 'vue';
export const gogoBackendStatus = Vue.observable({
    projectId: '',
    status: '',
    backendInfo: null,
    buildLog: [],
    // 后端运行时告警（运维提示）
    alerts: [],
    unreadAlertCount: 0,
    // 当前激活的环境，由 dialog 切 env 时同步；告警拉取/标记已读都按这个 env 过滤，
    // 避免"在 prod 看 dev 告警"或"全部已读误把其他环境也标了"
    currentEnvId: 'dev',
    dialog: {
        show: false,
        left: 0,
        top: 0,
        width: 350,
        height: 380,
    },
    reset(projectId, status, backendInfo) {
        this.projectId = projectId;
        this.status = status;
        this.backendInfo = backendInfo;
    },
    /** 同步当前激活的环境，由 dialog 在 envId 变化时调用 */
    setCurrentEnvId(envId) {
        this.currentEnvId = envId || 'dev';
    },
    async fetchAlerts(envId) {
        if (!this.projectId)
            return;
        const targetEnvId = envId !== undefined ? envId : this.currentEnvId;
        try {
            const { gogoBackendAlertAPI } = await import('../../backendAPI');
            const resp = await gogoBackendAlertAPI.getAlerts({ projectId: this.projectId, envId: targetEnvId });
            this.alerts = resp.alerts;
            this.unreadAlertCount = resp.unreadCount;
        }
        catch (error) {
            console.error('[xy-client] fetchAlerts failed', error);
        }
    },
    async markAlertRead(alertId) {
        if (!this.projectId)
            return;
        try {
            const { gogoBackendAlertAPI } = await import('../../backendAPI');
            await gogoBackendAlertAPI.markAlertRead({ projectId: this.projectId, alertId, envId: this.currentEnvId });
            // 本地立即更新，避免等下次轮询
            this.alerts = this.alerts.map(a => (a.id === Number(alertId) ? { ...a, isRead: true } : a));
            this.unreadAlertCount = Math.max(0, this.unreadAlertCount - 1);
        }
        catch (error) {
            console.error('[xy-client] markAlertRead failed', error);
        }
    },
    async markAllAlertsRead() {
        if (!this.projectId)
            return;
        try {
            const { gogoBackendAlertAPI } = await import('../../backendAPI');
            await gogoBackendAlertAPI.markAllAlertsRead({ projectId: this.projectId, envId: this.currentEnvId });
            this.alerts = this.alerts.map(a => ({ ...a, isRead: true }));
            this.unreadAlertCount = 0;
        }
        catch (error) {
            console.error('[xy-client] markAllAlertsRead failed', error);
        }
    },
});
new Vue({
    setup() {
        watch(() => `${gogoBackendStatus.status}-${gogoBackendStatus.backendInfo?.buildLogDate}`, (_1, _2, onCleanup) => {
            let stopped = false;
            let timer;
            let buildLogReader;
            onCleanup(() => {
                stopped = true;
                if (timer)
                    clearTimeout(timer);
                buildLogReader?.cancel().catch(() => { });
            });
            gogoBackendStatus.buildLog = [];
            const readBuildLogStreamOnce = (async () => {
                const { gogoBackendBuildAPI, projectAPI } = await import('../../backendAPI');
                const { projectId } = gogoBackendStatus;
                if (!projectId)
                    return false;
                const buildLogStream = await gogoBackendBuildAPI.getBuildLogStream(projectId, gogoBackendStatus.backendInfo?.buildLogDate || '');
                if (stopped)
                    return;
                const buildLog = [];
                const reader = buildLogStream.getReader();
                buildLogReader = reader;
                let lastItem;
                try {
                    while (!stopped) {
                        const { done, value } = await reader.read();
                        if (done)
                            break;
                        const item = JSON.parse(value);
                        lastItem = item;
                        buildLog.push(item);
                        // 服务端重连后返回全量日志，因此每次连接内累积后整体覆盖，避免重复追加。
                        gogoBackendStatus.buildLog = Object.freeze([...buildLog]);
                    }
                }
                finally {
                    if (buildLogReader === reader)
                        buildLogReader = undefined;
                    reader.releaseLock();
                }
                if (lastItem && lastItem.type !== 'log' && lastItem.type !== 'start' && lastItem.type !== 'heartbeat') {
                    // refetch status!
                    projectAPI.getProjectInfo();
                    return true;
                }
                return false;
            });
            if (gogoBackendStatus.status !== 'deploying') {
                readBuildLogStreamOnce();
                return;
            }
            const pollLoop = async () => {
                while (!stopped) {
                    try {
                        const finished = await readBuildLogStreamOnce();
                        if (finished)
                            return;
                    }
                    catch (error) {
                        if (error?.code !== -101) {
                            console.error('[xy-client] poll backend build log failed', error);
                        }
                    }
                    if (stopped)
                        return;
                    await new Promise((resolve) => {
                        timer = setTimeout(resolve, 500);
                    });
                }
            };
            pollLoop();
        }, { immediate: true });
        // 告警轮询：弹窗打开时拉取一次 + 每 30s；弹窗关闭时每 60s 轻量轮询未读数
        watch(() => `${gogoBackendStatus.projectId}-${gogoBackendStatus.dialog.show}-${gogoBackendStatus.currentEnvId}`, (_1, _2, onCleanup) => {
            let stopped = false;
            let timer;
            onCleanup(() => {
                stopped = true;
                if (timer)
                    clearTimeout(timer);
            });
            if (!gogoBackendStatus.projectId)
                return;
            const poll = async () => {
                if (stopped)
                    return;
                try {
                    await gogoBackendStatus.fetchAlerts();
                }
                catch (error) {
                    console.error('[xy-client] poll alerts failed', error);
                }
                if (stopped)
                    return;
                // 弹窗打开时 30s 一次（避免高频打 DB），关闭时 60s 一次
                const interval = gogoBackendStatus.dialog.show ? 30_000 : 60_000;
                timer = setTimeout(poll, interval);
            };
            poll();
        }, { immediate: true });
    },
});
